import { localDateOnly } from '../../utils/localDate';
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './StartPlanModal.css';

const StartPlanModal = ({ plan, reader, onStart, onClose }) => {
  const { preferences } = useAuth();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const [targetEndDate, setTargetEndDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [useSmartDefaults, setUseSmartDefaults] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!useSmartDefaults) {
      if (!targetEndDate) {
        setError('Please select a target end date');
        return;
      }

      const selectedEndDate = new Date(targetEndDate);
      const selectedStartDate = startDate ? new Date(startDate) : new Date();
      
      if (selectedEndDate <= selectedStartDate) {
        setError('Target end date must be after start date');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');
      
      const options = useSmartDefaults ? {} : {
        targetEndDate,
        ...(startDate ? { customStartDate: startDate } : {})
      };
      
      await onStart(plan.id, reader.id, options);
    } catch (err) {
      console.error('Error starting plan:', err);
      setError(err.response?.data?.error || 'Failed to start plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return localDateOnly(tomorrow);
  };

  const getSmartDefaultDates = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // October reading focus dates
    const octoberStart = new Date(currentYear, 8, 28); // September 28th (0-indexed month)
    const octoberEnd = new Date(currentYear, 10, 2);   // November 2nd (0-indexed month)
    
    // If we're before September 28th, default to October focus dates
    if (now < octoberStart) {
      return {
        startDate: localDateOnly(octoberStart),
        endDate: localDateOnly(octoberEnd),
        description: 'October reading focus (Sept 28 - Nov 2)'
      };
    }
    
    // If we're after September 28th but before November 2nd, use current date to November 2nd
    if (now >= octoberStart && now <= octoberEnd) {
      return {
        startDate: localDateOnly(now),
        endDate: localDateOnly(octoberEnd),
        description: 'Current to November 2nd'
      };
    }
    
    // If we're after November 2nd, use current date + estimated days
    if (plan.estimatedDays) {
      const suggestedEnd = new Date();
      suggestedEnd.setDate(suggestedEnd.getDate() + plan.estimatedDays);
      return {
        startDate: localDateOnly(now),
        endDate: localDateOnly(suggestedEnd),
        description: `Current + ${getEstimatedDaysText(plan.estimatedDays)}`
      };
    }
    
    return {
      startDate: localDateOnly(now),
      endDate: '',
      description: 'Current date (flexible end)'
    };
  };

  const getSuggestedEndDate = () => {
    if (!plan.estimatedDays) return '';
    
    const suggestedDate = new Date();
    suggestedDate.setDate(suggestedDate.getDate() + plan.estimatedDays);
    return localDateOnly(suggestedDate);
  };

  const getEstimatedDaysText = (days) => {
    if (!days) return 'Flexible duration';
    if (days === 1) return '1 day';
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.round(days / 7)} weeks`;
    if (days < 365) return `${Math.round(days / 30)} months`;
    return `${Math.round(days / 365)} years`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="start-plan-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Start plan</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          <div className="plan-summary">
            <h3>{plan.name}</h3>
            <p className="plan-description">
              {plan.description || 'A structured plan to guide your Bible study journey.'}
            </p>
            
            <div className="plan-details">
              <div className="detail-item">
                <span className="detail-label">Reader:</span>
                <span className="detail-value">{reader.name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Chapters:</span>
                <span className="detail-value">{plan.chapterCount || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Estimated duration</span>
                <span className="detail-value">{getEstimatedDaysText(plan.estimatedDays)}</span>
              </div>
              {competitiveMode && (
                <div className="detail-item">
                  <span className="detail-label">Points reward</span>
                  <span className="detail-value">{plan.pointsReward || 0} points</span>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="start-plan-form">
            <div className="form-group">
              <div className="smart-defaults-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={useSmartDefaults}
                    onChange={(e) => setUseSmartDefaults(e.target.checked)}
                  />
                  <span className="toggle-text">Use smart defaults</span>
                </label>
                {useSmartDefaults && (
                  <div className="smart-defaults-info">
                    <span className="smart-defaults-badge">🎯</span>
                    <span className="smart-defaults-description">
                      {getSmartDefaultDates().description}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {!useSmartDefaults && (
              <>
                <div className="form-group">
                  <label htmlFor="startDate">
                    Start date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={targetEndDate || undefined}
                    className="date-input"
                  />
                  <p className="help-text">
                    When would you like to start this plan? (Optional - defaults to today)
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="targetEndDate">
                    Target completion date *
                  </label>
                  <input
                    type="date"
                    id="targetEndDate"
                    value={targetEndDate}
                    onChange={(e) => setTargetEndDate(e.target.value)}
                    min={startDate || getMinDate()}
                    required
                    className="date-input"
                  />
                  {plan.estimatedDays && (
                    <button
                      type="button"
                      className="suggested-date-button"
                      onClick={() => setTargetEndDate(getSuggestedEndDate())}
                    >
                      Use suggested date ({getEstimatedDaysText(plan.estimatedDays)})
                    </button>
                  )}
                  <p className="help-text">
                    Choose when you'd like to complete this plan. The app will automatically adjust your daily reading requirements to help you stay on track.
                  </p>
                </div>
              </>
            )}

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="start-button"
                disabled={loading || (!useSmartDefaults && !targetEndDate)}
              >
                {loading ? 'Starting plan...' : (useSmartDefaults ? 'Start plan with smart defaults' : 'Start plan')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StartPlanModal;
